// Sugalarm — status orb (cycle 214; interior restyled cycle 215).
//
// The single living speaking indicator on the Now screen: a dimensional
// swirling-plasma glass sphere (Variant B, "Twilight"). It is also the
// tap-to-speak / tap-to-stop control (the interactive <button> is the ≥44px
// hit target). Purely presentational — all behavior (what `state` is, what a
// tap does) is owned by the NowScreen wiring; this component only renders the
// visual for the given `state` and forwards taps. The `state` machine, the
// per-variant `size`, the emitted shell rings, and the under-orb caption
// (owned by NowScreen) are unchanged from cycle 214 — cycle 215 changes only
// the pixels inside the circle.
//
// NOT the cycle-16 `VoiceOrb` bars in `KioskHeader` (still live, untouched).
//
// Interior = a layered, dimensional plasma sphere, painted back→front:
//   dark core · back filaments (dim/blurred) · core veil · front filaments
//   (sharp/bright) · form shadow · specular gloss.
// Cycle 222: the hard glass-rim ring is gone — a radial MASK fades the plasma
// to transparent before the disc edge, and the outer glow lives on a separate
// unclipped/unmasked .glow layer so the halo never gets sliced into a box.
// Filament stroke widths + blur radii are in SVG viewBox units so they scale
// with the orb and stay legible at every mounted size. Colors come from the
// `--orb-*` Twilight tokens only (no hardcoded hex); motion is transform/
// opacity only (GPU-composited). `prefers-reduced-motion` and an active
// urgent alarm resolve to the frozen (static, swirl-paused) look — the depth
// layers stay so the orb is still a dimensional sphere when frozen.

import { useId } from "react";

import styles from "./StatusOrb.module.css";

export type StatusOrbState = "idle" | "speaking" | "frozen";

interface Props {
  /** idle → slow swirl + gentle breathe; speaking → ~2× faster swirl +
   *  faster breathe + emitted shell rings; frozen → swirl paused, breathe
   *  off, depth layers intact (urgent overlay or reduced motion). */
  state: StatusOrbState;
  /** Orb diameter in px. Per-variant sizing lives in NowScreen. */
  size?: number;
  /** Tap handler. The orb always forwards the tap; the no-op-while-frozen
   *  rule (no manual speak over an urgent overlay) is enforced by the
   *  handler NowScreen passes, not here. */
  onTap?: () => void;
  /** Accessible label — "Speak status" / "Stop speaking", chosen upstream. */
  ariaLabel?: string;
}

export function StatusOrb({ state, size = 56, onTap, ariaLabel }: Props) {
  // Unique filter ids so two orbs on one page can't collide on a global id.
  const uid = useId().replace(/:/g, "");
  const blurBack = `orbBlurBack-${uid}`;
  const blurFront = `orbBlurFront-${uid}`;

  return (
    <button
      type="button"
      className={styles.orbBtn}
      data-state={state}
      style={{ ["--orb-size" as string]: `${size}px` }}
      onClick={onTap}
      aria-label={ariaLabel ?? "Speak status"}
      aria-pressed={state === "speaking"}
    >
      <span className={styles.orb} aria-hidden="true">
        {/* Emitted shell rings — animate only in the speaking state (cycle
            214 behavior, preserved); painted behind the sphere so they read
            as a pulse emanating from the ball's edge. */}
        <span className={`${styles.shell} ${styles.shell1}`} />
        <span className={`${styles.shell} ${styles.shell2}`} />
        <span className={`${styles.shell} ${styles.shell3}`} />

        {/* Cycle 222: the OUTER glow lives on its own layer with NO
            overflow/clip/mask/filter, so the halo renders fully outside the
            disc and fades softly in every direction (never sliced into a
            box). It rides the breathe scale with the sphere. */}
        <span className={styles.glow} />

        {/* The dimensional plasma sphere. `isolation: isolate` (in CSS) keeps
            the screen-blended filaments blending only against the layers
            inside the ball, never the page. `overflow:hidden` + the round
            shape clip every layer to the circle; a radial MASK (cycle 222)
            fades the plasma to transparent BEFORE the disc boundary so the
            ball dissolves into its glow with no crisp circular cut. */}
        <span className={styles.sphere}>
          {/* 1 · Dark translucent glass core. */}
          <span className={styles.core} />

          {/* 2 · Back filament set — dimmer + more blurred, behind the veil. */}
          <svg
            className={styles.wispsBack}
            viewBox="0 0 100 100"
            preserveAspectRatio="xMidYMid meet"
          >
            <defs>
              <filter
                id={blurBack}
                x="-40%"
                y="-40%"
                width="180%"
                height="180%"
              >
                <feGaussianBlur stdDeviation="2.6" />
              </filter>
            </defs>
            <g className={styles.spinBack} filter={`url(#${blurBack})`}>
              <path className={styles.wB1} d="M12,62 C30,28 70,22 90,48" />
              <path className={styles.wB2} d="M16,40 C40,74 64,70 86,38" />
            </g>
          </svg>

          {/* 3 · Core veil — sits between the wisp sets so the back filaments
              read as behind and the front as in front (the 3-D cue). */}
          <span className={styles.veil} />

          {/* 4 · Front filament set — sharper + brighter, in front of veil. */}
          <svg
            className={styles.wispsFront}
            viewBox="0 0 100 100"
            preserveAspectRatio="xMidYMid meet"
          >
            <defs>
              <filter
                id={blurFront}
                x="-40%"
                y="-40%"
                width="180%"
                height="180%"
              >
                <feGaussianBlur stdDeviation="1.05" />
              </filter>
            </defs>
            <g className={styles.spinFront} filter={`url(#${blurFront})`}>
              <path className={styles.wF1} d="M20,58 C40,26 62,30 82,54" />
              <path className={styles.wF2} d="M22,44 C44,72 60,66 80,40" />
              <path className={styles.wF3} d="M30,72 C44,46 58,52 74,28" />
            </g>
          </svg>

          {/* 5 · Form shadow — non-animated volume: lit upper-left, pooled
              lower-right. */}
          <span className={styles.form} />

          {/* 6 · Specular highlight — glossy curved-glass cue, upper-left. */}
          <span className={styles.gloss} />

          {/* Cycle 222: the hard glass rim ring + its inner glow are GONE —
              the plasma now dissolves at the masked edge into the external
              .glow halo, so there is no crisp circular boundary. */}
        </span>
      </span>
    </button>
  );
}
